import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import HistoryIcon from "@mui/icons-material/History";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import "./subscriptionPlans.css";
import BASE_URL from "../../config";

let stripePromise = null;

function SubscriptionPlansContent() {
  const stripe = useStripe();
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem("jwt_token");

  // Fetch subscription status
  const fetchSubscriptionStatus = () => {
    fetch(`${BASE_URL}/api/subscription/status/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setSubscriptionStatus(data);
      })
      .catch((err) => {
        console.error("Error fetching subscription status:", err);
      });
  };

  // Fetch billing history
  const fetchBillingHistory = () => {
    fetch(`${BASE_URL}/api/subscription/billing-history/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then(setBillingHistory)
      .catch((err) => {
        console.error("Error fetching billing history:", err);
      });
  };

  useEffect(() => {
    if (token) {
      fetchSubscriptionStatus();
      fetchBillingHistory();
    }
  }, [token]);

  // Fetch current plan
  useEffect(() => {
    if (!token) return;

    fetch(`${BASE_URL}/api/current-plan/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Not authenticated or failed to fetch");
        return res.json();
      })
      .then((data) => {
        const id = data.planId ?? null;
        setCurrentPlanId(id);
      })
      .catch((err) => {
        console.error("Failed to fetch current plan:", err);
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch plans
  useEffect(() => {
    fetch(`${BASE_URL}/api/subscription-plans/`)
      .then((res) => res.json())
      .then((data) => {
        setPlans(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to fetch plans:", err);
        toast.error("Failed to load subscription plans.");
      })
      .finally(() => setPlansLoading(false));
  }, []);

  const handleSubscribe = async (planId) => {
    // Only block if plan matches AND subscription is active
    const isCurrentActivePlan = planId === currentPlanId && subscriptionStatus?.status === "active";
    if (isCurrentActivePlan || !token) return;

    if (!stripe) {
      toast.error("Payment system not ready. Please refresh the page.");
      return;
    }

    setSubscribing(planId);

    try {
      // Step 1: Create subscription on backend
      const response = await fetch(`${BASE_URL}/api/subscribe/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription failed");
      }

      const data = await response.json();

      console.log("=== SUBSCRIPTION RESPONSE ===");
      console.log("Client Secret:", data.client_secret ? "Present" : "Missing");
      console.log("Payment Method ID:", data.payment_method_id || "NULL");
      console.log("Payment Status:", data.payment_status);
      console.log("=============================");

      // Step 2: Check if payment confirmation is needed
      if (data.client_secret) {
        // Check if user has a payment method
        if (!data.payment_method_id) {
          throw new Error("Please add a payment method before subscribing. Go to Settings → Payment Methods to add a card.");
        }

        toast.info("Confirming payment...");
        console.log("Attempting to confirm payment with method:", data.payment_method_id);

        // Confirm the payment with Stripe
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.client_secret,
          {
            // Use the default payment method already attached to the customer
            payment_method: data.payment_method_id,
          }
        );

        console.log("Payment confirmation result:", {
          error: confirmError ? confirmError.message : null,
          status: paymentIntent ? paymentIntent.status : "unknown"
        });

        if (confirmError) {
          console.error("Payment confirmation error:", confirmError);
          throw new Error(confirmError.message);
        }

        if (paymentIntent.status === "succeeded") {
          console.log("Payment succeeded!");
          toast.success("Subscription activated and payment confirmed!");
        } else if (paymentIntent.status === "processing") {
          console.log("Payment is processing (Direct Debit)");
          toast.success("Subscription created! Payment is processing (3-5 business days). Your subscription will activate once payment is confirmed.");
        } else if (paymentIntent.status === "requires_action") {
          console.log("Payment requires additional action (3D Secure)");
          // Handle 3D Secure or other authentication
          const { error: authError } = await stripe.confirmCardPayment(data.client_secret);
          if (authError) {
            console.error("3D Secure auth error:", authError);
            throw new Error(authError.message);
          }
          toast.success("Subscription activated!");
        } else {
          console.warn("Unexpected payment status:", paymentIntent.status);
          toast.warning(`Payment status: ${paymentIntent.status}. Please check your subscription.`);
        }
      } else if (data.payment_status === "processing") {
        // Direct Debit payment - processing status
        console.log("Direct Debit payment processing");
        toast.success("Subscription created! Payment is processing (3-5 business days). Your subscription will activate once payment is confirmed.");
      } else {
        // No payment confirmation needed (e.g., trial period)
        console.log("No payment confirmation needed");
        toast.success(data.message || "Subscription successful!");
      }

      setCurrentPlanId(planId);

      // Wait for webhook to process, then refresh (increased to 5 seconds)
      setTimeout(() => {
        fetchSubscriptionStatus();
        fetchBillingHistory();
      }, 5000);

    } catch (err) {
      console.error("Subscription error:", err);
      toast.error(err.message || "Failed to subscribe. Please add a payment method first.");
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = () => {
    setActionLoading(true);

    fetch(`${BASE_URL}/api/subscription/cancel/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ immediate: cancelImmediate }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to cancel subscription");
        return res.json();
      })
      .then((data) => {
        toast.success(data.message || "Subscription canceled successfully");
        setShowCancelModal(false);
        fetchSubscriptionStatus();
      })
      .catch((err) => {
        console.error("Cancel error:", err);
        toast.error("Failed to cancel subscription. Please try again.");
      })
      .finally(() => setActionLoading(false));
  };

  const handleReactivateSubscription = () => {
    setActionLoading(true);

    fetch(`${BASE_URL}/api/subscription/reactivate/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to reactivate subscription");
        return res.json();
      })
      .then((data) => {
        toast.success(data.message || "Subscription reactivated successfully");
        fetchSubscriptionStatus();
      })
      .catch((err) => {
        console.error("Reactivate error:", err);
        toast.error("Failed to reactivate subscription. Please try again.");
      })
      .finally(() => setActionLoading(false));
  };

  const handleChangePlan = () => {
    if (!selectedPlanId) return;
    setActionLoading(true);

    fetch(`${BASE_URL}/api/subscription/change-plan/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ planId: selectedPlanId, prorate: true }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to change plan");
        return res.json();
      })
      .then((data) => {
        toast.success(data.message || "Plan changed successfully");
        setShowChangePlanModal(false);
        setCurrentPlanId(selectedPlanId);
        fetchSubscriptionStatus();
        fetchBillingHistory();
      })
      .catch((err) => {
        console.error("Change plan error:", err);
        toast.error("Failed to change plan. Please try again.");
      })
      .finally(() => setActionLoading(false));
  };

  const handleRetryPayment = () => {
    setActionLoading(true);

    fetch(`${BASE_URL}/api/subscription/retry-payment/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to retry payment");
        return res.json();
      })
      .then((data) => {
        toast.success(data.message || "Payment retry initiated");
        fetchSubscriptionStatus();
        fetchBillingHistory();
      })
      .catch((err) => {
        console.error("Retry payment error:", err);
        toast.error("Failed to retry payment. Please try again.");
      })
      .finally(() => setActionLoading(false));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: { label: "Succeeded", className: "status-success" },
      paid: { label: "Paid", className: "status-success" },
      pending: { label: "Pending", className: "status-pending" },
      open: { label: "Open", className: "status-pending" },
      processing: { label: "Processing", className: "status-processing" },
      awaiting_confirmation: { label: "Awaiting Confirmation", className: "status-processing" },
      failed: { label: "Failed", className: "status-failed" },
      refunded: { label: "Refunded", className: "status-refunded" },
    };

    const config = statusConfig[status] || { label: status, className: "status-default" };
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  const getSubscriptionStatusConfig = (status) => {
    const configs = {
      active: {
        color: "#4caf50",
        icon: <CheckCircleIcon />,
        text: "Active",
        actions: ["cancel", "changePlan"],
      },
      past_due: {
        color: "#ff9800",
        icon: <WarningIcon />,
        text: "Payment Failed",
        message: "Your payment failed. Please update your payment method or retry.",
        actions: ["retryPayment"],
      },
      canceled: {
        color: "#9e9e9e",
        icon: <CancelIcon />,
        text: "Canceled",
        message: `Your subscription is canceled and will end on ${
          subscriptionStatus?.next_billing_date
            ? new Date(subscriptionStatus.next_billing_date).toLocaleDateString()
            : "the end of the period"
        }`,
        actions: ["reactivate"],
      },
      suspended: {
        color: "#f44336",
        icon: <CancelIcon />,
        text: "Suspended",
        message: "Your subscription has been suspended due to non-payment.",
        actions: ["reactivate"],
      },
    };

    return configs[status] || configs.active;
  };

  const SkeletonCard = () => <div className="plan-card skeleton" />;

  return (
    <div className="subscription-container-dark">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="page-header">
        <h1>My Subscription</h1>
        <p className="page-subtitle">Manage your subscription and billing</p>
      </div>

      {/* Subscription Status Card */}
      {subscriptionStatus && (
        <div className="info-cards-grid">
          <div className="info-card subscription-status-card">
            <div className="card-header">
              {getSubscriptionStatusConfig(subscriptionStatus.status).icon}
              <h3>Subscription Status</h3>
            </div>
            <div className="card-content">
              <div
                className="status-badge-large"
                style={{
                  backgroundColor: getSubscriptionStatusConfig(subscriptionStatus.status).color,
                }}
              >
                {getSubscriptionStatusConfig(subscriptionStatus.status).text}
              </div>

              {getSubscriptionStatusConfig(subscriptionStatus.status).message && (
                <p className="status-message">
                  {getSubscriptionStatusConfig(subscriptionStatus.status).message}
                </p>
              )}

              <div className="subscription-details">
                <div className="detail-row">
                  <span className="label">Plan</span>
                  <span className="value">{subscriptionStatus.plan_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Monthly Price</span>
                  <span className="value">£{Number(subscriptionStatus.plan_price).toFixed(2)}</span>
                </div>
                {subscriptionStatus.next_billing_date && (
                  <div className="detail-row">
                    <span className="label">Next Billing Date</span>
                    <span className="value">
                      {new Date(subscriptionStatus.next_billing_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {subscriptionStatus.grace_period_end && (
                  <div className="detail-row">
                    <span className="label">Grace Period Ends</span>
                    <span className="value highlight-warning">
                      {new Date(subscriptionStatus.grace_period_end).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                {getSubscriptionStatusConfig(subscriptionStatus.status).actions.includes("cancel") && (
                  <button
                    className="action-btn cancel-btn"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel Subscription
                  </button>
                )}
                {getSubscriptionStatusConfig(subscriptionStatus.status).actions.includes("changePlan") && (
                  <button
                    className="action-btn change-plan-btn"
                    onClick={() => setShowChangePlanModal(true)}
                  >
                    Change Plan
                  </button>
                )}
                {getSubscriptionStatusConfig(subscriptionStatus.status).actions.includes("reactivate") && (
                  <button
                    className="action-btn reactivate-btn"
                    onClick={handleReactivateSubscription}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Reactivating..." : "Reactivate Subscription"}
                  </button>
                )}
                {getSubscriptionStatusConfig(subscriptionStatus.status).actions.includes("retryPayment") && (
                  <button
                    className="action-btn retry-btn"
                    onClick={handleRetryPayment}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Retrying..." : "Retry Payment"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing History */}
      <div className="history-card">
        <div
          className="card-header clickable"
          onClick={() => setShowBillingHistory(!showBillingHistory)}
        >
          <HistoryIcon className="card-icon" />
          <h3>Billing History</h3>
          <span className="toggle-icon">{showBillingHistory ? "▼" : "▶"}</span>
        </div>
        {showBillingHistory && (
          <div className="card-content">
            {billingHistory.length > 0 ? (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Plan</th>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                        <td>{invoice.subscription_plan}</td>
                        <td>
                          {new Date(invoice.period_start).toLocaleDateString()} -{" "}
                          {new Date(invoice.period_end).toLocaleDateString()}
                        </td>
                        <td>£{Number(invoice.amount_due).toFixed(2)}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-message">No billing history yet.</p>
            )}
          </div>
        )}
      </div>

      {/* Subscription Plans */}
      <div className="plans-section">
        <h2 className="section-title">Available Plans</h2>
        <div className="plans-grid">
          {loading || plansLoading
            ? [...Array(3)].map((_, i) => <SkeletonCard key={i} />)
            : plans.length > 0
            ? plans.map((plan) => {
                // Only mark as current if plan matches AND subscription is active
                const isCurrent = plan.id === currentPlanId &&
                  subscriptionStatus?.status === "active";
                const isLoading = subscribing === plan.id;

                return (
                  <div
                    key={plan.id}
                    className={`plan-card ${isCurrent ? "active" : ""}`}
                  >
                    {isCurrent && <div className="ribbon">Current Plan</div>}

                    <div className="plan-header">
                      <WorkspacePremiumIcon className="plan-icon" />
                      <h3>{plan.name}</h3>
                      <p className="plan-price">£{plan.price}/month</p>
                      <p className="plan-billing">Billed monthly</p>
                    </div>
                    <ul className="plan-features">
                      {plan.features &&
                        plan.features.map((feature, index) => <li key={index}>✓ {feature}</li>)}
                    </ul>
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isCurrent || isLoading}
                      className={`subscribe-btn ${
                        isCurrent ? "disabled" : isLoading ? "loading" : ""
                      }`}
                    >
                      {isCurrent ? "Active" : isLoading ? "Subscribing..." : "Subscribe"}
                    </button>
                  </div>
                );
              })
            : null}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Cancel Subscription?</h2>
            <p>Choose when to cancel your subscription:</p>

            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  checked={!cancelImmediate}
                  onChange={() => setCancelImmediate(false)}
                />
                <span>
                  Cancel at period end (
                  {subscriptionStatus?.next_billing_date
                    ? new Date(subscriptionStatus.next_billing_date).toLocaleDateString()
                    : "end of period"}
                  )
                </span>
                <p className="radio-description">You'll keep access until then</p>
              </label>

              <label>
                <input
                  type="radio"
                  checked={cancelImmediate}
                  onChange={() => setCancelImmediate(true)}
                />
                <span>Cancel immediately</span>
                <p className="radio-description">Access ends now, no refund</p>
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCancelModal(false)}>
                Go Back
              </button>
              <button
                className="btn-danger"
                onClick={handleCancelSubscription}
                disabled={actionLoading}
              >
                {actionLoading ? "Canceling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="modal-overlay" onClick={() => setShowChangePlanModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Change Plan</h2>
            <p>Select a new plan:</p>

            <div className="plan-selection">
              {plans
                .filter((p) => p.id !== currentPlanId)
                .map((plan) => (
                  <label key={plan.id} className="plan-option">
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlanId === plan.id}
                      onChange={() => setSelectedPlanId(plan.id)}
                    />
                    <div className="plan-info">
                      <span className="plan-name">{plan.name}</span>
                      <span className="plan-price-small">£{plan.price}/month</span>
                    </div>
                  </label>
                ))}
            </div>

            <p className="proration-note">
              The price difference will be prorated on your next invoice. No immediate charge is made.
            </p>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowChangePlanModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleChangePlan}
                disabled={!selectedPlanId || actionLoading}
              >
                {actionLoading ? "Changing..." : "Change Plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrapper component with Stripe Elements
export default function SubscriptionPlans() {
  const [stripePublicKey, setStripePublicKey] = useState(null);
  const token = localStorage.getItem("jwt_token");

  useEffect(() => {
    const fetchStripeConfig = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/subscription/payment/config/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.stripe_public_key) {
            setStripePublicKey(data.stripe_public_key);
            if (!stripePromise) {
              stripePromise = loadStripe(data.stripe_public_key);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching Stripe config:", error);
      }
    };

    if (token) {
      fetchStripeConfig();
    }
  }, [token]);

  if (!stripePublicKey || !stripePromise) {
    return (
      <div className="subscription-container-dark">
        <div className="page-header">
          <h1>My Subscription</h1>
          <p className="page-subtitle">Loading payment system...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <SubscriptionPlansContent />
    </Elements>
  );
}
